import React, { useEffect, useMemo, useState } from 'react';
import { Plus, Download, Eye, Edit, Briefcase } from 'lucide-react';
import toast from 'react-hot-toast';
import { usePagination, useFilters, useSearch, useCrud } from '../../hooks/useApi';
import { contactService } from '../../services/api';
import FormPageShell from '../../components/Common/FormPageShell';
import PageHeader from '../../components/Common/PageHeader';
import Can from '../../components/Common/Can';
import { getAppHomePath } from '../../constants/navigation';
import { P } from '../../constants/permissions';
import PositionModal from '../../components/Contact/PositionModal';
import PositionTable from '../../components/Contact/PositionTable';
import PositionFilters from '../../components/Contact/PositionFilters';
import { downloadJson } from '../../utils/exportHelpers';
import { useTranslation } from 'react-i18next';
import { userFacingError } from '../../i18n/userFacingError';

const API_LEVEL_TO_FORM = {
  junior: 'JUNIOR',
  pleno: 'PLENO',
  mid: 'PLENO',
  senior: 'SENIOR',
  lead: 'LEAD',
  manager: 'LEAD',
};

const FORM_LEVEL_TO_API = {
  JUNIOR: 'junior',
  PLENO: 'pleno',
  SENIOR: 'senior',
  LEAD: 'lead',
};

const toBool = (v) => v === true || v === 'true';

function mapApiToUi(row) {
  const lev = String(row.level || '').toLowerCase();
  return {
    id: row.id,
    name: row.name,
    description: row.description || '',
    level: API_LEVEL_TO_FORM[lev] || 'JUNIOR',
    isActive: row.is_active,
    createdAt: row.created_at,
  };
}

const Positions = () => {
  const { t } = useTranslation('contact');

  const [showModal, setShowModal] = useState(false);
  const [selectedPosition, setSelectedPosition] = useState(null);
  const [viewMode, setViewMode] = useState('table');

  const pagination = usePagination(1, 10);
  const filters = useFilters({
    name: '',
    level: '',
    isActive: '',
  });
  const search = useSearch();

  const { useList, useCreate, useUpdate, useDelete } = useCrud('positions', contactService.positionCrud);
  const { data: listData, isLoading, error, refetch } = useList();
  const createMutation = useCreate();
  const updateMutation = useUpdate();
  const deleteMutation = useDelete();

  const rawList = useMemo(() => {
    const d = listData;
    return Array.isArray(d) ? d : d?.items ?? d?.data ?? [];
  }, [listData]);

  const mapped = useMemo(() => rawList.map(mapApiToUi), [rawList]);

  const filtered = useMemo(() => {
    let rows = mapped;
    const n = (filters.filters.name || '').trim().toLowerCase();
    if (n) rows = rows.filter((r) => (r.name || '').toLowerCase().includes(n));
    if (filters.filters.level) {
      rows = rows.filter((r) => r.level === filters.filters.level);
    }
    if (filters.filters.isActive === 'true') rows = rows.filter((r) => r.isActive === true);
    if (filters.filters.isActive === 'false') rows = rows.filter((r) => r.isActive === false);
    const q = (search.query || '').trim().toLowerCase();
    if (q) {
      rows = rows.filter(
        (r) =>
          (r.name || '').toLowerCase().includes(q) || (r.description || '').toLowerCase().includes(q)
      );
    }
    return rows;
  }, [mapped, filters.filters, search.query]);

  useEffect(() => {
    pagination.setTotal(filtered.length);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtered.length]);

  const paged = useMemo(() => {
    const start = (pagination.page - 1) * pagination.limit;
    return filtered.slice(start, start + pagination.limit);
  }, [filtered, pagination.page, pagination.limit]);

  const handleCreatePosition = () => {
    setSelectedPosition(null);
    setShowModal(true);
  };

  const handleEditPosition = (position) => {
    setSelectedPosition(position);
    setShowModal(true);
  };

  const handleViewPosition = (position) => {
    toast(
      [`Nome: ${position.name}`, `Nível: ${position.level}`, position.description ? `Descrição: ${position.description}` : '']
        .filter(Boolean)
        .join('\n'),
      { duration: 6000 }
    );
  };

  const handleDeletePosition = (position) => {
    if (!window.confirm('Excluir este cargo?')) return;
    deleteMutation.mutate(position.id, {
      onSuccess: () => {
        setShowModal(false);
        setSelectedPosition(null);
      },
      onError: (e) => toast.error(userFacingError(e, 'deleteError')),
    });
  };

  const handleSave = (data) => {
    const apiLevel = FORM_LEVEL_TO_API[data.level] || 'junior';
    if (selectedPosition) {
      updateMutation.mutate(
        {
          id: selectedPosition.id,
          data: {
            name: data.name,
            description: data.description || null,
            level: apiLevel,
            is_active: toBool(data.isActive),
          },
        },
        {
          onSuccess: () => {
            setShowModal(false);
            setSelectedPosition(null);
          },
          onError: (e) => toast.error(userFacingError(e, 'updateError')),
        }
      );
    } else {
      createMutation.mutate(
        {
          name: data.name,
          description: data.description || null,
          level: apiLevel,
          is_active: toBool(data.isActive),
        },
        {
          onSuccess: () => setShowModal(false),
          onError: (e) => toast.error(userFacingError(e, 'createError')),
        }
      );
    }
  };

  const handleExport = () => {
    downloadJson(`cargos-${new Date().toISOString().slice(0, 10)}.json`, filtered);
    toast.success('Exportação iniciada');
  };

  const getStatusBadge = (isActive) => (
    <span className={`badge ${isActive ? 'badge-success' : 'badge-danger'}`}>{isActive ? 'Ativo' : 'Inativo'}</span>
  );

  const getLevelBadge = (level) => {
    const levelConfig = {
      JUNIOR: { label: 'Junior', color: 'info' },
      PLENO: { label: 'Pleno', color: 'primary' },
      SENIOR: { label: 'Sênior', color: 'success' },
      LEAD: { label: 'Lead', color: 'warning' },
    };
    const config = levelConfig[level] || levelConfig.JUNIOR;
    return <span className={`badge badge-${config.color}`}>{config.label}</span>;
  };

  if (error) {
    return (
      <FormPageShell>
        <div className="rounded-lg border border-danger-200 bg-danger-50 p-4 text-danger-800">
          {t('common:loadError')}{' '}
          <button type="button" className="btn btn-outline btn-sm ml-2" onClick={() => refetch()}>
            Tentar novamente
          </button>
        </div>
      </FormPageShell>
    );
  }

  return (
    <FormPageShell>
      <div className="space-y-6">
        <PageHeader
          title={t("positionsTitle")}
          description="Cargos e níveis hierárquicos para organização interna."
          breadcrumbs={[{ label: 'Início', to: getAppHomePath() }, { label: 'Cargos' }]}
        >
          <Can permission={P.CONTACT_ACCESS}>
            <div className="flex flex-wrap items-center gap-2 sm:gap-3">
              <button type="button" className="btn btn-outline shadow-sm" onClick={handleExport} disabled={!filtered.length}>
                <Download className="mr-2 h-4 w-4" />
                <span className="hidden sm:inline">Exportar</span>
              </button>
              <button
                type="button"
                onClick={handleCreatePosition}
                className="btn btn-primary inline-flex items-center gap-2 shadow-sm"
              >
                <Plus className="h-4 w-4" />
                Novo cargo
              </button>
            </div>
          </Can>
        </PageHeader>

        <div className="data-list-card">
          <div className="border-b border-gray-100 bg-gradient-to-r from-slate-50/80 to-white px-5 py-4 sm:px-6">
            <PositionFilters filters={filters} search={search} />
          </div>

          <div className="space-y-4 p-5 sm:p-6">
            {isLoading && <p className="text-sm text-gray-600">Carregando…</p>}
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setViewMode('table')}
                  className={`btn ${viewMode === 'table' ? 'btn-primary' : 'btn-outline'} btn-sm`}
                >
                  Tabela
                </button>
                <button
                  type="button"
                  onClick={() => setViewMode('cards')}
                  className={`btn ${viewMode === 'cards' ? 'btn-primary' : 'btn-outline'} btn-sm`}
                >
                  Cards
                </button>
              </div>
              <div className="text-sm text-gray-600">Mostrando {filtered.length} cargo(s)</div>
            </div>

            {viewMode === 'table' ? (
              <PositionTable
                positions={paged}
                totalCount={filtered.length}
                pagination={pagination}
                onView={handleViewPosition}
                onEdit={handleEditPosition}
                onDelete={handleDeletePosition}
                getStatusBadge={getStatusBadge}
                getLevelBadge={getLevelBadge}
                onCreate={handleCreatePosition}
              />
            ) : (
              <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
                {paged.map((position) => (
                  <div key={position.id} className="card">
                    <div className="card-content">
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center text-primary-600">
                            <Briefcase className="w-5 h-5" />
                          </div>
                          <div>
                            <h3 className="font-semibold text-gray-900">{position.name}</h3>
                            <p className="text-sm text-gray-600">{position.description}</p>
                          </div>
                        </div>
                        <div className="flex flex-col gap-1">
                          {getStatusBadge(position.isActive)}
                          {getLevelBadge(position.level)}
                        </div>
                      </div>

                      <div className="space-y-2 mb-4">
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-500">Criado em:</span>
                          <span className="text-gray-900">
                            {position.createdAt ? new Date(position.createdAt).toLocaleDateString('pt-BR') : '—'}
                          </span>
                        </div>
                      </div>

                      <Can permission={P.CONTACT_ACCESS}>
                        <div className="flex gap-2">
                          <button type="button" onClick={() => handleViewPosition(position)} className="btn btn-outline btn-sm flex-1">
                            <Eye className="w-4 h-4 mr-1" />
                            Ver
                          </button>
                          <button type="button" onClick={() => handleEditPosition(position)} className="btn btn-outline btn-sm flex-1">
                            <Edit className="w-4 h-4 mr-1" />
                            Editar
                          </button>
                        </div>
                      </Can>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {showModal && (
        <PositionModal
          key={selectedPosition?.id ?? 'new'}
          position={selectedPosition}
          onClose={() => {
            setShowModal(false);
            setSelectedPosition(null);
          }}
          onSave={handleSave}
        />
      )}
    </FormPageShell>
  );
};

export default Positions;
